import type { Dayjs } from 'dayjs'
import { Array, Option, pipe } from 'effect'
import { z } from 'zod'

// HourlyFlags のスキーマ: 実際に使用する時刻のみ定義する
const hourlyFlagsSchema = z
  .object({
    '0': z.boolean().optional(), // SnapshotSchedule で使用
    '17': z.boolean().optional(), // CommitmentKpiSchedule で使用
  })
  .partial()
type DaySchedule = z.infer<typeof hourlyFlagsSchema>
// const hourKeySchema = hourlyFlagsSchema.keyof()
// type HourKey = keyof typeof hourKeySchema.enum

const dayScheduleSchema = hourlyFlagsSchema

const weekScheduleSchema = z.object({
  sun: hourlyFlagsSchema.optional(),
  mon: hourlyFlagsSchema.optional(),
  tue: hourlyFlagsSchema.optional(),
  wed: hourlyFlagsSchema.optional(),
  thu: hourlyFlagsSchema.optional(),
  fri: hourlyFlagsSchema.optional(),
  sat: hourlyFlagsSchema.optional(),
})
export type WeekSchedule = z.infer<typeof weekScheduleSchema>
export const dayOfWeekKeySchema = weekScheduleSchema.keyof()
export type DayOfWeekKey = keyof typeof dayOfWeekKeySchema.enum

const monthScheduleSchema = z.object({
  '1': hourlyFlagsSchema.optional(),
  '2': hourlyFlagsSchema.optional(),
  '3': hourlyFlagsSchema.optional(),
  '4': hourlyFlagsSchema.optional(),
  '5': hourlyFlagsSchema.optional(),
  '6': hourlyFlagsSchema.optional(),
  '7': hourlyFlagsSchema.optional(),
  '8': hourlyFlagsSchema.optional(),
  '9': hourlyFlagsSchema.optional(),
  '10': hourlyFlagsSchema.optional(),
  '11': hourlyFlagsSchema.optional(),
  '12': hourlyFlagsSchema.optional(),
  '13': hourlyFlagsSchema.optional(),
  '14': hourlyFlagsSchema.optional(),
  '15': hourlyFlagsSchema.optional(),
  '16': hourlyFlagsSchema.optional(),
  '17': hourlyFlagsSchema.optional(),
  '18': hourlyFlagsSchema.optional(),
  '19': hourlyFlagsSchema.optional(),
  '20': hourlyFlagsSchema.optional(),
  '21': hourlyFlagsSchema.optional(),
  '22': hourlyFlagsSchema.optional(),
  '23': hourlyFlagsSchema.optional(),
  '24': hourlyFlagsSchema.optional(),
  '25': hourlyFlagsSchema.optional(),
  '26': hourlyFlagsSchema.optional(),
  '27': hourlyFlagsSchema.optional(),
  '-1': hourlyFlagsSchema.optional(),
})
type MonthSchedule = z.infer<typeof monthScheduleSchema>
export const dayOfMonthKeySchema = monthScheduleSchema.keyof()
export type DayOfMonthKey = keyof typeof dayOfMonthKeySchema.enum

/**
 * Interface for a schedule entry that defines when to take snapshots
 */
export const scheduleEntrySchema = z.object({
  timezone: z.string(), // e.g., 'Asia/Tokyo', '+9 hours'
  day: dayScheduleSchema.optional(),
  week: weekScheduleSchema.optional(),
  month: monthScheduleSchema.optional(),
  // year?: {
  //   // To be defined
  // }
})
export type ScheduleEntry = z.infer<typeof scheduleEntrySchema>

// 安全にObjectから値を取り出すには面倒だけどこの書き方になる
const getHourlyFlagsWithWeek =
  (week: WeekSchedule) =>
  (nowWithTimezone: Dayjs): DaySchedule | undefined => {
    const dow = nowWithTimezone.day()
    switch (dow) {
      case 0: {
        return week.sun
      }
      case 1: {
        return week.mon
      }
      case 2: {
        return week.tue
      }
      case 3: {
        return week.wed
      }
      case 4: {
        return week.thu
      }
      case 5: {
        return week.fri
      }
      case 6: {
        return week.sat
      }
    }
  }

const getHourlyFlagsWithMonth =
  (month: MonthSchedule) =>
  (nowWithTimezone: Dayjs): DaySchedule | undefined => {
    // 常に「date <= daysInMonth」なので、「date - daysInMonth <= 0」は常に成り立つ
    const [date, daysInMonth] = [nowWithTimezone.date(), nowWithTimezone.daysInMonth()]

    return pipe(
      Object.entries(month),
      Array.findFirst(([k]) => k === date.toString() || k === (date - daysInMonth - 1).toString()),
      Option.map(([, v]) => v),
      Option.getOrUndefined,
    )
  }

export const shouldExecuteCronEntry = (entry: ScheduleEntry, now: Dayjs): boolean => {
  const { timezone, day, week, month } = entry
  const nowWithTimezone = now.tz(timezone)

  const applicableHourlyFlags = [
    day,
    week === undefined ? undefined : getHourlyFlagsWithWeek(week)(nowWithTimezone),
    month === undefined ? undefined : getHourlyFlagsWithMonth(month)(nowWithTimezone),
  ].flatMap((flags) => (flags === undefined ? [] : [flags]))

  if (applicableHourlyFlags.length === 0) return false

  // 今の段階で時刻まで見るとdev環境等で動かなくなるので登録がどこかにあれば起動させる(Event Bridge Scheduleが1日1回しか来ない前提)
  // day / week / month いずれかが一致すれば実行する
  return applicableHourlyFlags.some((flags) => Object.values(flags).some(Boolean))

  // 時刻も設定できるようになったらこちらに切替
  // const currentHour = nowWithTimezone.hour()
  // return applicableHourlyFlags.some((flags) =>
  //   Object.entries(flags).some(([hour, flag]) => hour === currentHour.toString() && flag)
  // )
}
